import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, OPS_ROLES } from "@/lib/constants";
import {
  LayoutDashboard, Truck, Package, Users, Bell, LogOut, Menu, X, Route, PlusCircle, MapPin, RadioTower,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: "all" },
  { to: "/jobs", label: "Jobs", icon: Package, roles: "all" },
  { to: "/fleet-map", label: "Live Map", icon: RadioTower, roles: [...OPS_ROLES, "am", "salesperson"] },
  { to: "/my-route", label: "My Route", icon: Route, roles: ["driver"] },
  { to: "/jobs/new", label: "New Job", icon: PlusCircle, roles: OPS_ROLES },
  { to: "/admin/trucks", label: "Fleet", icon: Truck, roles: OPS_ROLES },
  { to: "/admin/users", label: "Team", icon: Users, roles: ["admin"] },
  { to: "/notifications", label: "Alerts", icon: Bell, roles: "all" },
];

function initials(name = "") {
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = NAV.filter((n) => n.roles === "all" || n.roles.includes(user.role));

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const SidebarInner = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-6 py-6">
        <img src="/stark-logo-white.png" alt="STARK" className="h-6 w-auto" />
        <div className="h-6 w-px bg-white/15" />
        <div className="text-[11px] font-medium text-white/50">Fleet ETA</div>
      </div>

      <nav className="flex-1 space-y-1 px-3 py-2">
        {items.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-all duration-200 ${
                  isActive
                    ? "bg-stark-terracotta text-white shadow-md shadow-stark-terracotta/25"
                    : "text-white/60 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <Icon className="h-[18px] w-[18px]" strokeWidth={2.2} />
              {n.label}
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-white/10 p-3">
        <div className="flex items-center gap-3 rounded-xl px-3 py-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-stark-moss text-xs font-bold text-white">
            {initials(user.name)}
          </div>
          <div className="min-w-0 flex-1 leading-tight">
            <div className="truncate text-sm font-semibold text-white">{user.name}</div>
            <div className="truncate text-[11px] text-white/50">{ROLE_LABELS[user.role] || user.role}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="rounded-lg p-2 text-white/50 transition-colors hover:bg-white/10 hover:text-white"
            title="Sign out"
          >
            <LogOut className="h-[18px] w-[18px]" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-stark-surface">
      {/* Desktop sidebar */}
      <aside className="stark-glass-dark fixed inset-y-0 left-0 hidden w-64 flex-col lg:flex" style={{ background: "#2b2d2b" }}>
        {SidebarInner}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64" style={{ background: "#2b2d2b" }}>
            {SidebarInner}
          </aside>
        </div>
      )}

      <div className="flex flex-1 flex-col lg:pl-64">
        {/* Top bar */}
        <header className="stark-glass sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-stark-border px-4 lg:px-8">
          <button
            className="rounded-lg p-2 text-stark-ink hover:bg-black/5 lg:hidden"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-btn"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <div className="flex-1" />
          <div className="hidden items-center gap-2 rounded-full bg-white px-3.5 py-1.5 text-xs font-semibold text-stark-muted shadow-sm sm:flex">
            <span className="h-2 w-2 animate-pulse rounded-full bg-stark-success" />
            Live tracking active
          </div>
        </header>

        <main className="flex-1 px-4 py-6 lg:px-8 lg:py-8">{children}</main>
      </div>
    </div>
  );
}
